import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  lockScroll,
  unlockScroll,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const MOBILE_SWIPE_LOCK_THRESHOLD = 12;
const MOBILE_SWIPE_CHANGE_THRESHOLD = 50;
const MOBILE_SWIPE_DIRECTION_RATIO = 1.2;
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #swipeStartX = null;

  #swipeStartY = null;

  #swipeDeltaX = 0;

  #isHorizontalSwipe = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('touchstart', this.#handleSwipeStart, { capture: true, passive: true });
    this.refs.dialog.addEventListener('touchmove', this.#handleSwipeMove, { capture: true, passive: false });
    this.refs.dialog.addEventListener('touchend', this.#handleSwipeEnd, { capture: true, passive: false });
    this.refs.dialog.addEventListener('touchcancel', this.#resetSwipe, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('touchstart', this.#handleSwipeStart, true);
    this.refs.dialog.removeEventListener('touchmove', this.#handleSwipeMove, true);
    this.refs.dialog.removeEventListener('touchend', this.#handleSwipeEnd, true);
    this.refs.dialog.removeEventListener('touchcancel', this.#resetSwipe, true);
    unlockScroll(this.refs.dialog);
  }

  #handleSwipeStart = (event) => {
    if (!window.matchMedia('(max-width: 989px)').matches || event.touches.length !== 1) {
      this.#resetSwipe();
      return;
    }

    if (!(event.target instanceof Element) || !event.target.closest('.dialog-zoomed-gallery')) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#swipeStartX = touch.clientX;
    this.#swipeStartY = touch.clientY;
    this.#swipeDeltaX = 0;
    this.#isHorizontalSwipe = false;
  };

  #handleSwipeMove = (event) => {
    if (this.#swipeStartX === null || this.#swipeStartY === null || event.touches.length !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.#swipeStartX;
    const deltaY = touch.clientY - this.#swipeStartY;
    this.#swipeDeltaX = deltaX;

    if (
      !this.#isHorizontalSwipe &&
      Math.abs(deltaX) >= MOBILE_SWIPE_LOCK_THRESHOLD &&
      Math.abs(deltaX) > Math.abs(deltaY) * MOBILE_SWIPE_DIRECTION_RATIO
    ) {
      this.#isHorizontalSwipe = true;
    }

    if (!this.#isHorizontalSwipe) return;

    event.preventDefault();
    event.stopPropagation();
  };

  #handleSwipeEnd = (event) => {
    if (!this.#isHorizontalSwipe) {
      this.#resetSwipe();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (Math.abs(this.#swipeDeltaX) >= MOBILE_SWIPE_CHANGE_THRESHOLD) {
      const buttons = Array.from(this.refs.thumbnails.querySelectorAll('button'));
      const selectedIndex = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
      const currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
      const direction = this.#swipeDeltaX < 0 ? 1 : -1;
      const targetIndex = Math.min(Math.max(currentIndex + direction, 0), this.refs.media.length - 1);

      if (targetIndex !== currentIndex) this.selectThumbnail(targetIndex);
    }

    this.#resetSwipe();
  };

  #resetSwipe = () => {
    this.#swipeStartX = null;
    this.#swipeStartY = null;
    this.#swipeDeltaX = 0;
    this.#isHorizontalSwipe = false;
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const sourceImage = this.closest('media-gallery')
      ?.querySelectorAll('slideshow-slide')
      [index]?.querySelector('.product-media__image');
    const sourceUrl = sourceImage instanceof HTMLImageElement ? sourceImage.currentSrc || sourceImage.src : '';
    const matchingIndex = sourceUrl
      ? media.findIndex((item) => {
          const image = item.querySelector('.product-media__image');
          if (!(image instanceof HTMLImageElement)) return false;

          return (image.currentSrc || image.src).split('?')[0] === sourceUrl.split('?')[0];
        })
      : -1;
    const targetIndex = matchingIndex >= 0 ? matchingIndex : index;
    const targetImage = media[targetIndex];
    const targetThumbnail = thumbnails.children[targetIndex];

    const open = () => {
      dialog.showModal();
      lockScroll(dialog);

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    open();
    this.selectThumbnail(targetIndex, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    unlockScroll(dialog);
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
